import { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { useAuth } from "../components/AuthContext";
import { auth } from "../services/firebase";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  updateDoc,
  orderBy,
  query,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../services/firebase";

const Home = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        /* ---------- USER INVOICES ---------- */
        const invoiceSnap = await getDocs(
          query(
            collection(db, "users", user.uid, "invoices"),
            orderBy("createdAt", "desc"),
          ),
        );

        /* ---------- USER QUOTATIONS ---------- */
        const quotationSnap = await getDocs(
          query(
            collection(db, "users", user.uid, "quotations"),
            orderBy("createdAt", "desc"),
          ),
        );

        const invoiceData = invoiceSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          type: "INVOICE",
          payment: docSnap.data().payment || "Pending",
          ...docSnap.data(),
        }));

        const quotationData = quotationSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          type: "QUOTATION",
          payment: docSnap.data().payment || "Pending",
          ...docSnap.data(),
        }));

        const merged = [...invoiceData, ...quotationData].sort(
          (a, b) => b.createdAt?.seconds - a.createdAt?.seconds,
        );

        setInvoices(invoiceData);
        setQuotations(quotationData);
        setFilteredInvoices(merged);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleRegisterCompany = () => {
    navigate("/register-company");
  };

  const handleCreateInvoice = () => {
    navigate("/createinvoice");
  };

  const handleCreateQuotation = () => {
    navigate("/createquotation");
  };

  const handleEdit = (doc) => {
    if (doc.type === "INVOICE") {
      navigate(`/edit-invoice/${doc.id}`);
    } else {
      navigate(`/edit-quotation/${doc.id}`);
    }
  };

  const handleDeleteClick = (document) => {
    setDocToDelete(document);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete || !user) return;

    const subCol = docToDelete.type === "INVOICE" ? "invoices" : "quotations";

    await deleteDoc(doc(db, "users", user.uid, subCol, docToDelete.id));

    setFilteredInvoices((prev) => prev.filter((d) => d.id !== docToDelete.id));

    setShowDeleteConfirm(false);
    setDocToDelete(null);
  };

  const handleSearch = () => {
    const value = search.trim().toLowerCase();

    if (!value) {
      setFilteredInvoices([...invoices, ...quotations]);
      return;
    }

    const filtered = [...invoices, ...quotations].filter((doc) =>
      (doc.invoiceNumber || doc.quotationNumber || "")
        .toLowerCase()
        .includes(value),
    );

    setFilteredInvoices(filtered);
  };

  const handleView = (doc) => {
    if (doc.type === "INVOICE") {
      navigate(`/view-invoice/${doc.id}`);
    } else {
      navigate(`/view-quotation/${doc.id}`);
    }
  };

  const handlePaymentChange = async (inv, value) => {
    if (!user) return;

    try {
      const subCol = inv.type === "INVOICE" ? "invoices" : "quotations";

      await updateDoc(doc(db, "users", user.uid, subCol, inv.id), {
        payment: value,
      });

      setFilteredInvoices((prev) =>
        prev.map((item) =>
          item.id === inv.id ? { ...item, payment: value } : item,
        ),
      );
    } catch (err) {
      console.error("Payment update failed", err);
    }
  };

  /* ---------- COUNTS ---------- */

  const totalInvoices = invoices.length;
  const totalQuotations = quotations.length;
  const finalInvoices = invoices.filter((i) => i.status === "FINAL").length;
  const finalQuotations = quotations.filter((q) => q.status === "FINAL").length;
  const InvoiceRevenue = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const QuotationRevenue = quotations.reduce((s, q) => s + (q.total || 0), 0);

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.logo}>Invoice & Quotation Generator</h2>
          <p style={styles.subText}>Manage invoices & quotations</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.secondaryBtn} onClick={handleRegisterCompany}>
            Register Company
          </button>

          <button
            style={styles.logout}
            onClick={() => setShowLogoutConfirm(true)}
          >
            Logout
          </button>
        </div>
      </div>

      {/* DASHBOARD STATS */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <h4>Total Invoices</h4>
          <p style={styles.statValue}>{totalInvoices}</p>
        </div>

        <div style={styles.statCard}>
          <h4>Finalized Invoices</h4>
          <p style={styles.statValue}>{finalInvoices}</p>
        </div>

        <div style={styles.statCard}>
          <h4>Total Revenue From Invoices</h4>
          <p style={styles.statValue}>{InvoiceRevenue.toFixed(2)}</p>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.statCard}>
          <h4>Total Quotations</h4>
          <p style={styles.statValue}>{totalQuotations}</p>
        </div>

        <div style={styles.statCard}>
          <h4>Finalized Quotations</h4>
          <p style={styles.statValue}>{finalQuotations}</p>
        </div>

        <div style={styles.statCard}>
          <h4>Total Revenue From Quotations</h4>
          <p style={styles.statValue}>{QuotationRevenue.toFixed(2)}</p>
        </div>
      </div>

      {/* ACTION CARDS */}
      <div style={styles.cards}>
        <div style={styles.card}>
          <h3>Create Invoice</h3>
          <p style={styles.count}>{totalInvoices}</p>
          <button style={styles.primaryBtn} onClick={handleCreateInvoice}>
            Create Invoice
          </button>
        </div>

        <div style={styles.card}>
          <h3>Quotations</h3>
          <p style={styles.count}>{totalQuotations}</p>
          <button style={styles.primaryBtn} onClick={handleCreateQuotation}>
            Create Quotation
          </button>
        </div>
      </div>

      {/* SEARCH */}
      <div style={styles.searchRow}>
        <input
          style={styles.searchInput}
          placeholder="Search Invoice or Quotation number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button style={styles.searchBtn} onClick={handleSearch}>
          Search
        </button>
      </div>

      {/* RECENT DOCUMENTS */}
      <div style={styles.tableCard}>
        <h3>Recent Documents</h3>

        {loading ? (
          <p>Loading...</p>
        ) : filteredInvoices.length === 0 ? (
          <p style={{ marginTop: 10 }}>No documents found</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Number</th>
                <th style={styles.th}>From</th>
                <th style={styles.th}>To</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>GST</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Payment</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredInvoices.map((inv) => {
                const amount = inv.grandTotal || inv.subTotal || 0;
                const gst = inv.gstAmount || 0;
                const total = inv.total || inv.grandTotal || 0;

                return (
                  <tr key={inv.id}>
                    {/* Number */}
                    <td style={styles.td}>
                      {inv.type === "INVOICE"
                        ? inv.invoiceNumber
                        : inv.quotationNumber}
                    </td>

                    {/* From */}
                    <td style={styles.td}>
                      {inv.companyName?.split("\n")[0] || "-"}
                    </td>

                    {/* To */}
                    <td style={styles.td}>
                      {inv.toAddress?.split("\n")[0] || "-"}
                    </td>

                    {/* Date */}
                    <td style={styles.td}>
                      {inv.createdAt?.toDate().toLocaleDateString()}
                    </td>

                    {/* Amount */}
                    <td style={styles.td}>₹{total.toFixed(2)}</td>

                    {/* GST */}
                    <td style={styles.td}>₹{gst.toFixed(2)}</td>

                    {/* Total */}
                    <td style={styles.td}>₹{amount.toFixed(2)}</td>

                    {/* Status */}
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          background: inv.convertedFromQuotation
                            ? "#3b82f6" // blue for converted
                            : inv.status === "FINAL"
                              ? "#16a34a" // green
                              : "#facc15", // yellow
                        }}
                      >
                        {inv.convertedFromQuotation
                          ? "CONVERTED"
                          : inv.status || "DRAFT"}
                      </span>
                    </td>

                    {/* Payment Dropdown */}
                    <td style={styles.td}>
                      <select
                        style={{
                          ...styles.paymentSelect,
                          background:
                            (inv.payment || "Pending") === "Success"
                              ? "#dcfce7"
                              : "#fef9c3",
                          color:
                            (inv.payment || "Pending") === "Success"
                              ? "#166534"
                              : "#854d0e",
                          border:
                            (inv.payment || "Pending") === "Success"
                              ? "1px solid #22c55e"
                              : "1px solid #facc15",
                        }}
                        value={inv.payment || "Pending"}
                        onChange={(e) =>
                          handlePaymentChange(inv, e.target.value)
                        }
                      >
                        <option value="Pending">Pending</option>
                        <option value="Success">Success</option>
                      </select>
                    </td>

                    {/* Actions */}
                    <td style={styles.td}>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          style={styles.viewBtn}
                          onClick={() => handleView(inv)}
                        >
                          View
                        </button>

                        <button
                          style={styles.actionBtn}
                          onClick={() => handleEdit(inv)}
                        >
                          Edit
                        </button>

                        <button
                          style={styles.deleteBtn}
                          onClick={() => handleDeleteClick(inv)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* LOGOUT MODAL */}
      {showLogoutConfirm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>

            <div style={styles.modalActions}>
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button style={styles.logout} onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Confirm Delete</h3>

            <p style={{ marginTop: "8px" }}>
              Are you sure you want to delete this{" "}
              <strong>{docToDelete?.type}</strong>?
            </p>

            <div style={styles.modalActions}>
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>

              <button style={styles.dangerBtn} onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "32px",
    background: "linear-gradient(180deg, #f8fafc, #f1f5f9)",
    minHeight: "100vh",
    fontFamily: "Inter, system-ui, -apple-system",
  },

  /* ---------- HEADER ---------- */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "36px",
  },

  logo: {
    color: "#2563eb",
    fontSize: "26px",
    fontWeight: 700,
    letterSpacing: "-0.5px",
    marginBottom: "4px",
  },

  subText: {
    color: "#64748b",
    fontSize: "14px",
  },

  headerActions: {
    display: "flex",
    gap: "12px",
  },

  logout: {
    padding: "10px 16px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: 500,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(239,68,68,0.25)",
  },

  secondaryBtn: {
    padding: "10px 16px",
    background: "#e5e7eb",
    color: "#111827",
    border: "none",
    borderRadius: "10px",
    fontWeight: 500,
    cursor: "pointer",
  },

  /* ---------- STATS ---------- */

  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "28px",
  },

  statCard: {
    background: "#ffffff",
    padding: "24px",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
  },

  statTitle: {
    fontSize: "14px",
    color: "#64748b",
    marginBottom: "8px",
  },

  statValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#0f172a",
  },

  /* ---------- ACTION CARDS ---------- */

  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "30px",
  },

  card: {
    background: "#ffffff",
    padding: "26px",
    borderRadius: "18px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  },

  count: {
    fontSize: "28px",
    fontWeight: 700,
    margin: "10px 0",
    color: "#0f172a",
  },

  primaryBtn: {
    marginTop: "14px",
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "15px",
    cursor: "pointer",
    boxShadow: "0 10px 20px rgba(37,99,235,0.35)",
  },

  /* ---------- SEARCH ---------- */

  searchRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "24px",
  },

  searchInput: {
    flex: 1,
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    outline: "none",
  },

  searchBtn: {
    padding: "14px 20px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: 600,
    cursor: "pointer",
  },

  /* ---------- TABLE ---------- */

  tableCard: {
    background: "#ffffff",
    padding: "24px",
    borderRadius: "18px",
    boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 12px",
    marginTop: "14px",
    textAlign: "center",
  },

  statusBadge: {
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#111827",
  },

  actionBtn: {
    padding: "6px 10px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    cursor: "pointer",
    marginRight: "6px",
  },

  deleteBtn: {
    padding: "6px 10px",
    background: "#fee2e2",
    color: "#b91c1c",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  th: {
    padding: "12px",
    fontSize: "13px",
    color: "#ffffff",
    background: "#2563eb",
    fontWeight: 600,
    textAlign: "center", // 🔥 ADD
  },

  td: {
    padding: "14px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#0f172a",
    verticalAlign: "middle", // 🔥 ADD
  },
  paymentSelect: {
    padding: "6px 8px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    fontSize: "13px",
    cursor: "pointer",
  },

  viewBtn: {
    padding: "6px 10px",
    background: "#dbeafe",
    color: "#1d4ed8",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 500,
  },

  /* ---------- MODAL ---------- */

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    background: "#ffffff",
    padding: "26px",
    borderRadius: "18px",
    width: "320px",
    boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "24px",
  },
  dangerBtn: {
    padding: "10px 16px",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: 500,
    cursor: "pointer",
  },
};

export default Home;
